import { useTranslation } from 'react-i18next';

import Header from 'components/checkout/Header';
import Loader from 'legacy/common/components/layout/Loader';
import { CheckoutSteps } from '../../../types';
import { useCheckout } from '../../../context';
import TopBackgroundWrapper from 'components/checkout/TopBackgroundWrapper';
import OrgInfo from '../../OrgInfo';
import CenteredContainer from 'components/CenteredContainer';
import SummaryItem from './components/SummaryItem';
import PaymentOptions from 'components/checkout/PaymentOptions';
import ConfirmOrderButton from './components/ConfirmOrderButton';
import TransactionDetails from 'components/checkout/TransactionDetails';
import useTransactionDetails from '../../../context/hooks/useTransactionDetails';
import { Card } from 'legacy/common/types/models';

function Summary() {
  const { t } = useTranslation(['checkout', 'organizations']);
  const transactionDetails = useTransactionDetails();
  const {
    goToStep,
    organization,
    user,
    cart,
    isLoading,
    checkoutSession,
    paymentMethod
  } = useCheckout();

  const goBack = () => {
    goToStep(CheckoutSteps.ProductsList);
  };

  const addNewCard = (options?: { onSuccess: () => void }) => {
    paymentMethod.addNewCard({
      onSuccess: (newCardId: string, cards: Card[]) =>
        checkoutSession
          .updateCheckout({
            updatedCreditCardId: newCardId,
            updatedUserCards: cards
          })
          .finally(() => {
            options?.onSuccess?.();
          })
    });
  };

  const selectCard = (
    selectedCardId: string,
    options?: { onSuccess: () => void }
  ) => {
    paymentMethod.selectedCard.setSelectedCard(selectedCardId, (cardId) =>
      checkoutSession
        .updateCheckout({
          // Since we're using the previous way of handling payment methods, we need to update the credit card id not the stripe payment method ID.
          // Stripe payment method ID is computed BEFORE sending the request to the backend.
          updatedCreditCardId: cardId
        })
        .finally(() => {
          options?.onSuccess();
        })
    );
  };

  const selectBuyNowPayLater = (
    selectedBuyNowPayLater: 'klarna' | 'affirm' | null
  ) => {
    paymentMethod.selectedBuyNowPayLater.setSelectedBuyNowPayLater(
      selectedBuyNowPayLater,
      (buyNowPayLater) =>
        checkoutSession.updateCheckout({
          updatedStripePaymentMethodType: buyNowPayLater
        })
    );
  };

  return (
    <Loader isOverlay isLoading={isLoading}>
      <Header
        onBack={goBack}
        title={t('organizations:memberships.checkout.headerTitle')}
        subtitle={organization.name}
        isUserLoggedIn={!!user}
      />
      <TopBackgroundWrapper
        imageUrl={organization.coverImageUrl}
        hasTierNav={false}
        isUserLoggedIn={!!user}
        className="pb-8 md:pb-14"
      >
        <OrgInfo />
      </TopBackgroundWrapper>
      <CenteredContainer hasPadding={false} wideContent>
        <div className="px-4 gap-5 md:gap-8 flex flex-col md:flex-row pb-50">
          <div className="w-full md:w-2/3">
            <div className="md:max-w-[650px]">
              <h3 className="text-base md:text-lg font-semibold md:font-bold">
                {t('organizations:memberships.checkout.summaryTitle')}
              </h3>
              <div className="gap-5 flex flex-1 flex-col">
                <div className="border-b-2 border-neutral-200 pb-5">
                  <ul>
                    {Object.entries(cart.selectedProducts).map(
                      ([productId, selectedProduct]) => (
                        <SummaryItem
                          key={productId}
                          product={selectedProduct}
                        />
                      )
                    )}
                  </ul>
                </div>
                <TransactionDetails
                  checkoutSummaryData={transactionDetails}
                  total={checkoutSession.sessionData?.total || 0}
                  currency={checkoutSession.sessionData?.currency}
                  totalDetailsLabel="feesAndTax"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 md:w-1/3 min-w-[350px]">
            <PaymentOptions
              total={checkoutSession.sessionData?.total || 0}
              isLoadingCheckout={checkoutSession.isLoadingCheckout}
              currency={checkoutSession.sessionData?.currency}
              paymentMethod={paymentMethod}
              Actions={<ConfirmOrderButton />}
              addNewCard={addNewCard}
              selectCard={selectCard}
              selectBuyNowPayLater={selectBuyNowPayLater}
            />
          </div>
        </div>
      </CenteredContainer>
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-neutral-200 border-t-1 md:hidden">
        <ConfirmOrderButton />
      </div>
    </Loader>
  );
}

export default Summary;
